import React, { useState, useRef, useMemo, useEffect } from 'react'
import { Model } from 'survey-core'
import { Survey } from 'survey-react-ui'

import { useConvertSurvey } from '../hooks/useConvertSurvey'
import { useSubmitAnswers } from '../hooks/useSubmitAnswers'
import { useLoadProgress } from '../hooks/useLoadProgress'
import 'survey-core/i18n/traditional-chinese'
import { useSurveyParams } from '../context/SurveyParamsContext'
import {
  convertQuestionMappings,
  convertVisibleIf,
  convertNotRequired,
} from '../utils/convertVisibleIf'

const Agree = ({
  setAc,
  setTk,
  ac,
  tk,
  pid,
  setIsLoading,
  setCompletionMessage,
}) => {
  const { sfdSamplerId, sfdPId } = useSurveyParams()
  const [myAgreeJson, setMyAgreeJson] = useState(null)
  const userIdRef = useRef(null)
  const [imagesReadingTime, setImagesReadingTime] = useState(0)
  // 全部頁面都可按上一頁（'all' = 全部可翻頁、null = 全部不可、陣列 = 指定頁碼）
  const canShowPrevButtonPages = 'all'
  const choicesOrderRef = useRef({})
  const choiceIndexMapRef = useRef({})
  const bringOptionsMapRef = useRef({})
  // 暫存需加
  const [isReady, setIsReady] = useState(false)
  // web_auth_type=1/2 也要啟動暫存
  const [webAuthType, setWebAuthType] = useState(0)

  const completedHtml = `<h3>問卷正在提交中，請稍候。</h3><h3>請在成功頁面顯示後再離開。</h3><h4>如有填卷相關問題請洽：</h4> <a href='https://lin.ee/HWETcqy'>https://lin.ee/HWETcqy</a><h4 style='margin-top:50px'>填寫其他問卷：</h4> <a href=${window.location.origin}>${window.location.origin}</a>`

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const agreeSurvey = new Model(myAgreeJson)

  // 跳題 小心複選題要用陣列（題號 1-based，工具自動 -1）
  const visibleIfConditionsInit = convertVisibleIf({
    // 2-6-3-1~3、5-1-1~3 的「其他，請說明」為單選半開放選項（o_type 6），不需另設說明題
    // 3-1-1 父親國籍：選項2=(1-2)新住民 → 3-1-1-1-2；選項4=(2)外國籍 → 3-1-1-2；其餘直接到 3-1-2
    q20: '{q19} = 2',
    q21: '{q19} = 4',
    // 3-1-2 母親國籍（同上鏡像）；其餘直接到 3-2
    q23: '{q22} = 2',
    q24: '{q22} = 4',
    // 4-1 身心障礙證明選(2)無 → 跳過 4-2，直接到 4-3
    q32: '{q31} = 1',
    // 4-5 選(2)每天都沒吃 → 跳過 4-6，直接到 4-7
    // （選項2「(1) 有吃，一星期＿天」是半開放選項 o_type 6，值為 "other"）
    q36: '{q35} = "other"',
    // 2-1 父親(1)或母親(2)任一有勾就顯示 4-9（兩個都沒勾才隱藏）
    q39: '{q6} anyof [1, 2]',
    // 4-10 選(2)無 → 跳過 4-11，直接到 5-1-1
    q41: '{q40} = 1',
    // 5-5-1 拆題：q49 單選（1=(0)完全沒有上網、2=(1)曾經上過網），q50 為原複選的保護措施
    // 選(1)曾經上過網 才顯示 5-5-1 保護措施 ~ 5-5-3-2；選(0)完全沒有上網 → 直接到 6-1
    q50: '{q49} = 2',
    q51: '{q49} = 2',
    q52: '{q49} = 2',
    q53: '{q49} = 2',
    // 7-1-1 ~ 7-1-13：選項3=(B1)知道但從未用過 → 顯示 B1 題；選項4=(B2)知道且曾使用過 → 顯示 B2 題
    q61: '{q60} = 3',
    q62: '{q60} = 4',
    q64: '{q63} = 3',
    q65: '{q63} = 4',
    q67: '{q66} = 3',
    q68: '{q66} = 4',
    q70: '{q69} = 3',
    q71: '{q69} = 4',
    q73: '{q72} = 3',
    q74: '{q72} = 4',
    q76: '{q75} = 3',
    q77: '{q75} = 4',
    q79: '{q78} = 3',
    q80: '{q78} = 4',
    q82: '{q81} = 3',
    q83: '{q81} = 4',
    q85: '{q84} = 3',
    q86: '{q84} = 4',
    q88: '{q87} = 3',
    q89: '{q87} = 4',
    q91: '{q90} = 3',
    q92: '{q90} = 4',
    q94: '{q93} = 3',
    q95: '{q93} = 4',
    q97: '{q96} = 3',
    q98: '{q96} = 4',
    // 7-1-14 其他服務(q99)未填 → 跳過 7-1-14-B/B1/B2，直接到 7-2
    // 7-1-14-B 只有兩個選項：1=(B1)、2=(B2)
    q100: '{q99} notempty',
    q101: '({q99} notempty) and ({q100} = 1)',
    q102: '({q99} notempty) and ({q100} = 2)',
  })

  // 非必填(從 0 計算，convertNotRequired 吃 1-based)
  // 7-1-14（題號99）其他服務開放填答非必填，未填才能跳到 7-2
  const notRequired = convertNotRequired([99])

  const mappedRateChoice = null
  const pageBreakQuestions = null

  // 篩出/結束條件
  const triggersConditionsInit = null

  // 帶出: 帶入 (用1開始計算填這裡)
  const questionMappings = convertQuestionMappings({})

  const convertedSurvey = useConvertSurvey({
    survey: agreeSurvey,
    pid,
    setAc,
    setTk,
    ac,
    tk,
    setMyAgreeJson,
    imagesReadingTime,
    setImagesReadingTime,
    setIsLoading,
    completedHtml,
    visibleIfConditionsInit,
    notRequired,
    mappedRateChoice,
    pageBreakQuestions,
    triggersConditionsInit,
    questionMappings,
    bringOptionsMapRef,
    userIdRef,
    choiceIndexMapRef,
    sfdSamplerId,
    sfdPId,
    setWebAuthType,
  })

  const surveyModel = useMemo(() => {
    if (!convertedSurvey) return null
    return new Model(convertedSurvey)
  }, [convertedSurvey])

  const otherAnswersRef = useRef({})
  const noneAnswersRef = useRef({})
  const matrixAnswersRef = useRef({})
  const timestampsRef = useRef({})

  useLoadProgress({
    surveyModel: surveyModel,
    convertedSurvey: myAgreeJson,
    isReady,
    setIsLoading,
    pid,
    ac,
    tk,
    choiceIndexMapRef,
    choicesOrderRef,
    bringOptionsMapRef,
    otherAnswers: otherAnswersRef.current,
    noneAnswers: noneAnswersRef.current,
    matrixAnswers: matrixAnswersRef.current,
    timestamps: timestampsRef.current,
    sfdSamplerId,
    webAuthType,
  })

  useEffect(() => {
    if (surveyModel) {
      const id = setTimeout(() => setIsReady(true), 50)
      return () => clearTimeout(id)
    }
  }, [surveyModel])

  // 當 surveyModel 建立時，自動捲動到頂端
  useEffect(() => {
    if (surveyModel) {
      setTimeout(() => {
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' })
        const surveyContainer = document.querySelector('.sd-root-modern')
        if (surveyContainer) {
          surveyContainer.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [surveyModel])

  function helpFunction(survey, options) {
    // 參考 P231113152849 或 P231101165555
  }

  const endSurveyMsg = (survey) => {
    setCompletionMessage(
      `<div>感謝您的填答～歡迎轉發分享！<br><br>想參加更多問卷調查活動？手指點點就可以抽獎賺好康！<br>點擊下方按鈕加入 EZChoice APP<br>或複製下載連結 <a href="https://ezcapp.page.link/tAQv" target="_blank">https://ezcapp.page.link/tAQv</a><br>裡面還有更多填問卷拿購物金的活動唷～<br>各種好玩的問卷等你來玩！<br><br>想知道更多好康問卷、活動消息，請追蹤關注<br><a href="https://www.facebook.com/EasyCTRs" target="_blank">【輕鬆點點】粉絲專頁</a><br><br>加入我們的 <a href="https://lin.ee/HWETcqy" target="_blank">LINE@</a>，<br>隨時得到最新填問卷獲好禮的消息。<br><br><h4 style="text-align: center">請按「完成」前往下載 EZChoice 或填寫更多問卷</h4></div>`
    )
  }

  useSubmitAnswers({
    survey: surveyModel,
    pid,
    ac,
    tk,
    imagesReadingTime,
    setIsLoading,
    helpFunction,
    canShowPrevButtonPages,
    choicesOrderRef,
    choiceIndexMapRef,
    endSurveyMsg,
    bringOptionsMapRef,
    userIdRef,
    enableProgressSaving: webAuthType > 0 || !!sfdSamplerId,
    sfdSamplerId,
    webAuthType,
  })

  return (
    <>
      {surveyModel ? (
        <Survey model={surveyModel} key={pid} />
      ) : myAgreeJson ? (
        <Survey model={agreeSurvey} />
      ) : (
        <></>
      )}
    </>
  )
}

export default Agree
